"use client";

import { useRef, useState } from "react";

import type { MentionCandidate } from "./MentionCandidate";
import { insertMention } from "./insertMention";
import { pendingMention, type PendingMention } from "./pendingMention";
import { serializeMentions } from "./serializeMentions";
import { tokenizeMentions } from "./tokenizeMentions";
import { useIsomorphicLayoutEffect } from "./useIsomorphicLayoutEffect";

type Props = {
    /** 本文。`@[表示名](参照)` を含む原文で、**これが正本**である。 */
    value: string;
    onChange: (next: string) => void;
    /** 候補の母集団。呼び出し側が組む。 */
    candidates: readonly MentionCandidate[];
    placeholder?: string;
    /** 最小の高さを行数で指定する。既定 8。中身が増えれば伸びる。 */
    rows?: number;
    disabled?: boolean;
    id?: string;
};

/** 欄と同じ字送り。行送り 20px と、上下の余白 8px + 枠 1px の 2 辺ぶん。 */
const LINE = 20;
const FRAME = 18;

/** 塊の見た目。読むときと書くときで姿を変えない。 */
const CHIP =
    "mx-0.5 rounded bg-brand-light px-1 align-baseline font-medium text-brand-dark select-none";

/**
 * メンション付きの本文入力。**書く場所を言いながら対象を決めさせる。**
 *
 * `@` を書かないと文が成立しないため、宛先の無い指示を残せない。候補は呼び出し側が組む。
 *
 * 土台は contenteditable である。textarea では中身を飾れず、参照の生表記
 * （`@[主要取引先表](element:{A})` の後半）を人に読ませることになる。塊として選べず一文字ずつ
 * 壊れる問題も、候補が `@` から離れて出る問題も、そこから来ていた。設計:
 * docs/notes/設計ノート/2026-09-08 設計ノート メンション入力欄の作り替え.md
 *
 * **本文はコンポーネントの外が持つ。** ここが持つのはキャレット・候補の開閉・元に戻す履歴で、
 * どれも画面の状態である。本文の書き換えは純粋関数（{@link insertMention} 等）へ出してあり、
 * この欄を描かなくても同じ結果を作れる。
 */
export function MentionInput({
    value,
    onChange,
    candidates,
    placeholder,
    rows = 8,
    disabled,
    id,
}: Props) {
    const hostRef = useRef<HTMLDivElement | null>(null);
    /** 直前に自分が出した本文。外から来た値がこれと同じなら DOM を触らない。 */
    const lastRef = useRef<string>("");
    /** 変換中か。変換中に DOM を組み直すと変換が切れる。 */
    const composingRef = useRef(false);
    /** 打つ直前の姿。`beforeinput` で控え、入力が済んだら履歴へ積む。 */
    const beforeRef = useRef<Snapshot | null>(null);
    /** Esc で閉じた書きかけ。同じ書きかけのまま開き直さないために覚える。 */
    const dismissedRef = useRef<string | null>(null);
    /** 元に戻す履歴と、戻した先の履歴。 */
    const undoRef = useRef<Snapshot[]>([]);
    const redoRef = useRef<Snapshot[]>([]);
    /** 最新の操作を非 React のリスナーへ渡す口。 */
    const actionsRef = useRef<{ history: (to: "undo" | "redo") => void; hold: () => void }>({
        history: () => {},
        hold: () => {},
    });

    const [pending, setPending] = useState<Pending | null>(null);
    const [active, setActive] = useState(0);

    const shown =
        pending === null
            ? []
            : candidates
                  .filter((c) => pending.query.length === 0 || c.name.includes(pending.query))
                  .slice(0, 8);
    const picked = shown.length === 0 ? undefined : shown[Math.min(active, shown.length - 1)];

    // 外から来た値を DOM へ映す。**自分の打鍵が一周して戻ってきただけなら触らない。**
    // 触ると一文字ごとにキャレットが飛ぶ（呼び出し側は打鍵ごとに保存する作りである）。
    useIsomorphicLayoutEffect(() => {
        const host = hostRef.current;
        if (host === null || composingRef.current) return;
        if (lastRef.current === value) return;
        lastRef.current = value;
        const caret = document.activeElement === host ? caretOf(host) : null;
        paint(host, value);
        if (caret !== null) placeCaret(host, Math.min(caret, value.length));
    }, [value]);

    /** 本文を丸ごと差し替える。履歴・DOM・呼び出し側を同じ内容で揃える。 */
    function commit(next: string, caret: number, from: Snapshot | null): void {
        const host = hostRef.current;
        if (host === null) return;
        if (from !== null) {
            undoRef.current.push(from);
            redoRef.current = [];
        }
        lastRef.current = next;
        paint(host, next);
        placeCaret(host, caret);
        onChange(next);
        refresh(host, next);
    }

    /** 書きかけの `@…` と、候補を出す位置を測り直す。 */
    function refresh(host: HTMLElement, text: string): void {
        const caret = caretOf(host);
        const found = caret === null ? null : pendingMention(text, caret);
        if (found === null) {
            setPending(null);
            dismissedRef.current = null;
            return;
        }
        // **閉じた書きかけは開き直さない。** Esc の keydown で閉じても、続く keyup が同じ
        // 書きかけを見つけて出し直すので、閉じたことにならない。
        if (dismissedRef.current === dismissKey(text, found.at)) {
            setPending(null);
            return;
        }
        // 選択位置を戻すのは**書きかけが変わったときだけ**。毎回戻すと、↓ で選んだ直後の
        // keyup が先頭へ引き戻し、キーボードで候補を選べなくなる。
        const moved = pending === null || pending.at !== found.at || pending.query !== found.query;
        setPending({ ...found, rect: rectOf(host, found.at) });
        if (moved) setActive(0);
    }

    function handleInput(): void {
        const host = hostRef.current;
        if (host === null) return;
        const text = read(host);
        // **変換中は本文を確定させない。** 確定前の文字で保存や DOM の組み直しを起こすと変換が
        // 切れる。ただし候補の絞り込みだけは追いつかせる。`compositionupdate` は DOM へ入る前に
        // 来るので、そこで測ると絞り込みが一手遅れる（実測で「主要」と打ち終える前は「しゅ」で
        // 絞られていた）。変換中の `input` は反映後に来るので、こちらで拾う。
        if (composingRef.current) {
            refresh(host, text);
            return;
        }
        const before = beforeRef.current;
        beforeRef.current = null;
        if (before !== null) {
            undoRef.current.push(before);
            redoRef.current = [];
        }
        lastRef.current = text;
        onChange(text);
        refresh(host, text);
    }

    function handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>): void {
        // **変換中はキーを奪わない。** 変換の確定と候補の確定が Enter を取り合い、確定した
        // 瞬間に意図しない候補が入る。
        if (e.nativeEvent.isComposing) return;
        if (pending === null) return;
        if (e.key === "Escape") {
            const host = hostRef.current;
            if (host !== null) dismissedRef.current = dismissKey(read(host), pending.at);
            setPending(null);
            e.preventDefault();
            return;
        }
        if (shown.length === 0) return;
        if (e.key === "ArrowDown") {
            setActive((i) => (i + 1) % shown.length);
            e.preventDefault();
            return;
        }
        if (e.key === "ArrowUp") {
            setActive((i) => (i - 1 + shown.length) % shown.length);
            e.preventDefault();
            return;
        }
        if (e.key === "Enter" || e.key === "Tab") {
            if (picked !== undefined) insert(picked);
            e.preventDefault();
        }
    }

    /** 候補を本文へ挿し込む。本文の書き換えそのものは純粋関数が持つ。 */
    function insert(candidate: MentionCandidate): void {
        const host = hostRef.current;
        if (host === null) return;
        const text = read(host);
        const caret = caretOf(host);
        if (caret === null) return;
        const next = insertMention(text, caret, candidate);
        if (next === null) return;
        setPending(null);
        host.focus();
        commit(next.text, next.caret, { text, caret });
    }

    function handlePaste(e: React.ClipboardEvent<HTMLDivElement>): void {
        // **平文だけを受ける。** 書式つきで貼られると DOM の形が崩れ、読み出しが壊れる。
        e.preventDefault();
        const host = hostRef.current;
        if (host === null) return;
        const pasted = e.clipboardData.getData("text/plain");
        const text = read(host);
        const span = selectionOf(host);
        if (span === null) return;
        const next = text.slice(0, span.start) + pasted + text.slice(span.end);
        commit(next, span.start + pasted.length, { text, caret: span.start });
    }

    /**
     * 元に戻す・やり直す。**ブラウザーの履歴には相乗りしない。**
     *
     * 塊の組み立てで DOM を自分で書き換えるため、ブラウザーが持つ履歴とは食い違う。本文の
     * 正本が文字列なので、自前の履歴は本文とキャレットの組を積むだけで足りる。
     */
    function history(to: "undo" | "redo"): void {
        const host = hostRef.current;
        if (host === null) return;
        const from = to === "undo" ? undoRef.current : redoRef.current;
        const into = to === "undo" ? redoRef.current : undoRef.current;
        const step = from.pop();
        if (step === undefined) return;
        into.push({ text: read(host), caret: caretOf(host) ?? 0 });
        lastRef.current = step.text;
        paint(host, step.text);
        placeCaret(host, step.caret);
        onChange(step.text);
        setPending(null);
    }

    /** 打つ直前の姿を控える。戻したときに打つ前へ返るようにするため。 */
    function hold(): void {
        const host = hostRef.current;
        if (host === null || composingRef.current) return;
        beforeRef.current = { text: read(host), caret: selectionOf(host)?.start ?? 0 };
    }

    actionsRef.current = { history, hold };

    /**
     * `beforeinput` は素のリスナーで受ける。React の合成イベントでは `inputType` が届かない
     * ことがあり、元に戻すの取りこぼしが環境で変わる。
     */
    useIsomorphicLayoutEffect(() => {
        const host = hostRef.current;
        if (host === null) return undefined;
        const onBeforeInput = (e: Event): void => {
            const type = (e as InputEvent).inputType;
            if (type === "historyUndo" || type === "historyRedo") {
                e.preventDefault();
                actionsRef.current.history(type === "historyUndo" ? "undo" : "redo");
                return;
            }
            actionsRef.current.hold();
        };
        host.addEventListener("beforeinput", onBeforeInput);
        return () => host.removeEventListener("beforeinput", onBeforeInput);
    }, []);

    const open = pending !== null;

    /**
     * 候補が開いているあいだ、位置を測り直す合図を張る。欄はスクロールする領域の中にあり、
     * ペインの幅も利用者が変えられるので、`@` の画面上の位置は動く。
     */
    useIsomorphicLayoutEffect(() => {
        if (!open) return undefined;
        const host = hostRef.current;
        if (host === null) return undefined;
        const remeasure = (): void =>
            setPending((p) => (p === null ? null : { ...p, rect: rectOf(host, p.at) }));
        // スクロールは捕捉段で受ける。欄を包む領域が動いても届くようにするため。
        window.addEventListener("scroll", remeasure, true);
        window.addEventListener("resize", remeasure);
        return () => {
            window.removeEventListener("scroll", remeasure, true);
            window.removeEventListener("resize", remeasure);
        };
    }, [open]);

    return (
        <div className="relative">
            <div
                ref={hostRef}
                id={id}
                role="textbox"
                aria-multiline
                aria-label={placeholder}
                contentEditable={disabled !== true}
                suppressContentEditableWarning
                tabIndex={disabled === true ? undefined : 0}
                style={{ minHeight: rows * LINE + FRAME }}
                className={`w-full rounded border border-border-input px-3 py-2 text-sm break-words whitespace-pre-wrap focus:ring-1 focus:ring-interactive focus:outline-none ${
                    disabled === true
                        ? "bg-sidebar-bg text-text-muted"
                        : "bg-surface text-text-primary"
                }`}
                onInput={handleInput}
                onPaste={handlePaste}
                onKeyDown={handleKeyDown}
                onCompositionStart={() => {
                    composingRef.current = true;
                }}
                onCompositionEnd={() => {
                    composingRef.current = false;
                    handleInput();
                }}
                // キャレットの移動でも候補の要否が変わる。打鍵・クリックをまとめて受ける。
                onKeyUp={() => {
                    const host = hostRef.current;
                    if (host !== null && !composingRef.current) refresh(host, read(host));
                }}
                onClick={() => {
                    const host = hostRef.current;
                    if (host !== null) refresh(host, read(host));
                }}
                onBlur={() => setPending(null)}
            />
            {/* プレースホルダーは `:empty` では成立しない。空の contenteditable には `<br>` が
                残ることがあり、疑似要素が当たらなくなる。値が空かどうかで出し分ける。 */}
            {value.length === 0 && placeholder !== undefined && (
                <p className="pointer-events-none absolute top-2 left-3 text-sm text-text-muted">
                    {placeholder}
                </p>
            )}
            <div className="mt-1 flex items-center gap-2 text-[11px] text-text-muted">
                <span className="min-w-0 flex-1">{value.length.toLocaleString()} 字</span>
                {pending === null && <span>@ で対象を挙げられます</span>}
            </div>

            {pending !== null && (
                <Suggestions
                    rect={pending.rect}
                    shown={shown}
                    picked={picked}
                    total={candidates.length}
                    onPick={insert}
                />
            )}
        </div>
    );
}

/** 本文とキャレットの組。元に戻す履歴の 1 段。 */
type Snapshot = { text: string; caret: number };

/** 閉じた書きかけの覚え。本文か `@` の位置が変われば別物になり、また開く。 */
const dismissKey = (text: string, at: number): string => `${at}:${text}`;

/** 書きかけの `@…` と、その `@` の画面上の位置。 */
type Pending = PendingMention & { rect: DOMRect | null };

/**
 * 候補の箱。**`@` の位置へ重ねる。**
 *
 * タスクペインは iframe の内側なので、OS が描く IME の候補窓のように外へ出られない。下に
 * 置き場が無ければ `@` の上へ反転し、幅からはみ出す分は内側へ押し戻す。
 */
function Suggestions({
    rect,
    shown,
    picked,
    total,
    onPick,
}: {
    rect: DOMRect | null;
    shown: readonly MentionCandidate[];
    picked: MentionCandidate | undefined;
    total: number;
    onPick: (candidate: MentionCandidate) => void;
}) {
    /** 箱の幅と、伸びられる高さの上限。狭いペインでも本文を隠しすぎない値にしてある。 */
    const WIDTH = 240;
    const MAX = 220;
    const GAP = 2;
    const EDGE = 4;

    if (rect === null || typeof window === "undefined") return null;

    const below = window.innerHeight - rect.bottom - GAP;
    const above = rect.top - GAP;
    const flip = below < MAX && above > below;
    const room = Math.max(flip ? above : below, LINE);
    const left = Math.max(EDGE, Math.min(rect.left, window.innerWidth - WIDTH - EDGE));

    return (
        <div
            className="fixed z-50 overflow-y-auto rounded-md border border-border-subtle bg-surface shadow-[0_4px_12px_rgba(42,39,35,.12)]"
            style={{
                left,
                width: WIDTH,
                maxHeight: Math.min(MAX, room),
                ...(flip
                    ? { bottom: window.innerHeight - rect.top + GAP }
                    : { top: rect.bottom + GAP }),
            }}
        >
            {shown.length === 0 ? (
                <p className="px-3 py-2 text-[11px] text-text-muted">
                    {total === 0 ? "候補を読み込めていません" : "一致する対象がありません"}
                </p>
            ) : (
                shown.map((c) => (
                    <button
                        key={c.ref}
                        type="button"
                        className={`flex w-full cursor-pointer items-center gap-2 border-t border-text-primary/8 px-3 py-1.5 text-left first:border-t-0 hover:bg-sidebar-bg ${
                            c === picked ? "bg-sidebar-bg" : ""
                        }`}
                        onMouseDown={(e) => {
                            // 押した時点で欄からフォーカスが外れると、挿し込む先を見失う。
                            e.preventDefault();
                            onPick(c);
                        }}
                    >
                        {c.badge !== undefined && (
                            <span className="shrink-0 rounded bg-brand-light px-1.5 text-[11px] font-semibold text-brand-dark">
                                {c.badge}
                            </span>
                        )}
                        <span className="min-w-0 flex-1 truncate text-[13px]">{c.name}</span>
                        {c.note !== undefined && (
                            <span className="shrink-0 text-[11px] text-text-muted">{c.note}</span>
                        )}
                    </button>
                ))
            )}
        </div>
    );
}

/**
 * 本文を DOM へ組む。塊は `contenteditable="false"` の span にする。
 *
 * これだけでブラウザーは塊を一つとして扱い、Backspace 一回で丸ごと消える。壊れかけの参照が
 * 本文に残る現象がこの一点で消える。
 *
 * **塊の前後に空のテキストノードは置かない。** 行末に塊が来たときの打ちにくさを避ける定石だが、
 * 長さ 0 のノードが末尾に残ると、選択位置を組み立てる側がそこへ範囲を張って範囲外になる
 * （実測で `setBaseAndExtent` が `IndexSizeError` を投げた）。挿し込みは表記の後ろへ空白を
 * 足すので、候補から入れた塊の直後には必ず文字がある。塊が本文の末尾に残るのは、利用者が
 * その空白を消したときだけである。
 */
function paint(host: HTMLElement, text: string): void {
    host.textContent = "";
    for (const token of tokenizeMentions(text)) {
        if (token.kind === "text") {
            host.appendChild(document.createTextNode(token.text));
            continue;
        }
        const chip = document.createElement("span");
        chip.contentEditable = "false";
        chip.dataset.mentionRef = token.ref;
        chip.textContent = token.name;
        chip.className = CHIP;
        host.appendChild(chip);
    }
}

/**
 * DOM を本文へ戻す。
 *
 * 改行は正規化する。contenteditable で Enter を押すとブラウザーは `<div>` や `<br>` を入れる。
 * どの形で入っても読み出しで `\n` へ畳むほうが、入れる形を制御しようとするより壊れない。
 *
 * `dropTrailingBr` は末尾の `<br>` を読まない指定である。ブラウザーは空行を保つために末尾へ
 * `<br>` を置くので、そのまま読むと本文の末尾に改行が増え続ける。キャレットの位置を数えるときは
 * 断片の末尾が本物の改行でありうるので、この扱いを外す。
 */
function read(host: HTMLElement, dropTrailingBr = true): string {
    let out = "";
    const walk = (parent: Node): void => {
        const children = Array.from(parent.childNodes);
        children.forEach((child, i) => {
            const last = i === children.length - 1;
            if (child.nodeType === Node.TEXT_NODE) {
                out += child.nodeValue ?? "";
                return;
            }
            if (!(child instanceof HTMLElement)) return;
            const ref = child.dataset.mentionRef;
            if (ref !== undefined) {
                out += serializeMentions([{ kind: "mention", name: child.textContent ?? "", ref }]);
                return;
            }
            if (child.tagName === "BR") {
                if (!(last && dropTrailingBr)) out += "\n";
                return;
            }
            if (out.length > 0 && !out.endsWith("\n")) out += "\n";
            walk(child);
        });
    };
    walk(host);
    return out;
}

/** キャレットの位置を本文の文字数で表す。選択があるときは終端を返す。 */
function caretOf(host: HTMLElement): number | null {
    const selection = window.getSelection();
    if (selection === null || selection.rangeCount === 0) return null;
    const range = selection.getRangeAt(0);
    if (!host.contains(range.endContainer)) return null;
    return lengthTo(host, range.endContainer, range.endOffset);
}

/** 選択の範囲を本文の文字数で表す。 */
function selectionOf(host: HTMLElement): { start: number; end: number } | null {
    const selection = window.getSelection();
    if (selection === null || selection.rangeCount === 0) return null;
    const range = selection.getRangeAt(0);
    if (!host.contains(range.startContainer) || !host.contains(range.endContainer)) return null;
    return {
        start: lengthTo(host, range.startContainer, range.startOffset),
        end: lengthTo(host, range.endContainer, range.endOffset),
    };
}

/**
 * 欄の先頭から指定の DOM 位置までを本文へ直したときの長さ。
 *
 * 対応を自前で数え上げず、範囲を切り出して {@link read} へ通す。塊の長さは表記の長さで数える
 * という規則が 1 箇所に留まり、数え方が二重にならない。
 */
function lengthTo(host: HTMLElement, node: Node, offset: number): number {
    const range = document.createRange();
    range.setStart(host, 0);
    try {
        range.setEnd(node, offset);
    } catch {
        return read(host).length;
    }
    const box = document.createElement("div");
    box.appendChild(range.cloneContents());
    return read(box, false).length;
}

/** 本文の文字数で表した位置を、DOM の位置へ直す。 */
function locate(host: HTMLElement, offset: number): { node: Node; at: number } | null {
    let seen = 0;
    let found: { node: Node; at: number } | null = null;
    const walk = (parent: Node): void => {
        const children = Array.from(parent.childNodes);
        for (let i = 0; i < children.length; i++) {
            if (found !== null) return;
            const child = children[i];
            if (child.nodeType === Node.TEXT_NODE) {
                const len = (child.nodeValue ?? "").length;
                if (offset <= seen + len) {
                    found = { node: child, at: offset - seen };
                    return;
                }
                seen += len;
                continue;
            }
            if (!(child instanceof HTMLElement)) continue;
            const ref = child.dataset.mentionRef;
            if (ref !== undefined) {
                const len = serializeMentions([
                    { kind: "mention", name: child.textContent ?? "", ref },
                ]).length;
                // 塊の途中は指せない。手前へ寄せる。
                if (offset < seen + len) {
                    found = { node: parent, at: i };
                    return;
                }
                seen += len;
                continue;
            }
            if (child.tagName === "BR") {
                if (offset <= seen) {
                    found = { node: parent, at: i };
                    return;
                }
                seen += 1;
                continue;
            }
            walk(child);
        }
    };
    walk(host);
    return found;
}

/** キャレットを本文の指定位置へ置く。 */
function placeCaret(host: HTMLElement, offset: number): void {
    const spot = locate(host, offset);
    const range = document.createRange();
    if (spot === null) {
        range.selectNodeContents(host);
        range.collapse(false);
    } else {
        range.setStart(spot.node, spot.at);
        range.collapse(true);
    }
    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);
}

/**
 * 本文の指定位置にある 1 文字の矩形。候補を `@` の位置へ出すために使う。
 *
 * キャレットを畳んだ範囲では矩形が空で返ることがあるため、`@` の 1 文字を包んで測る。
 */
function rectOf(host: HTMLElement, offset: number): DOMRect | null {
    const spot = locate(host, offset);
    if (spot === null || spot.node.nodeType !== Node.TEXT_NODE) return null;
    const len = (spot.node.nodeValue ?? "").length;
    if (spot.at >= len) return null;
    const range = document.createRange();
    range.setStart(spot.node, spot.at);
    range.setEnd(spot.node, spot.at + 1);
    const rect = range.getBoundingClientRect();
    return rect.width === 0 && rect.height === 0 ? null : rect;
}
